import MDEditor from "@uiw/react-md-editor";
import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { setAlert } from "../../../actions/alert";
import { getRestaurantById } from "../../../actions/restaurants";
import checkAuthz from "../../../util/authz";
import { getRestaurantDetailsById } from "../../../util/getRestaurantInfo";
import { slugify } from "../../../util/slugify";
import { buildRestaurantMeta } from "../../../util/restaurantMetaBuilder";
import AddToListModal from "../AddToListModal";
import RestaurantDetailsLayout from "../../shared/RestaurantDetailsLayout";
import {
  GroupedMenuDisplay,
  getDisplayableMenuItems,
} from "../../shared/MenuDisplay";
import {
  getAnalyticsSource,
  logRestaurantWebsiteClick,
  logRestaurantDirectionsClick,
} from "../../../util/analytics";
import CheckUnauth from "../../auth/CheckUnauth";
import RestaurantEditForm from "../restaurantBuilder/restaurantBuilderLegacy/RestaurantEditForm";
import MenuRefreshPanel from "./MenuRefreshPanel";
import RestaurantOwnerAnalytics from "./RestaurantOwnerAnalytics";
import RestaurantDescriptionEditor from "./RestaurantDescriptionEditor";
import {
  CenteredContainer,
  CenteredContent,
  PageContainer,
  PageHeader,
  HeaderRow,
  HeaderLeft,
  PageTitle,
  PageSubtitle,
  RefreshButton,
  TabBar,
  Tab,
  AddToListButton,
  ContentCard,
  LoadingState,
} from "./restaurantDetailsStyles";

// --- Component ---

const VALID_TABS = ["view", "edit-listing", "edit-description", "analytics"];

const RestaurantDetails = ({
  getRestaurantById,
  restaurants,
  token,
  setAlert,
}) => {
  const { id, slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const restaurant = restaurants.find((r) => r._id === id);
  const computedSlug = restaurant ? slugify(restaurant.name) : slug;

  // Tab state from URL
  const rawTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "view";

  const setActiveTab = (tab) => {
    if (tab === "view") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Details data
  const [detailsData, setDetailsData] = useState({});
  const [showListModal, setShowListModal] = useState(false);

  // Refresh state (for edit-listing tab)
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedData, setRefreshedData] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  // Consume SSR data on mount (same pattern as SearchBar.js)
  const [ssrConsumed, setSsrConsumed] = useState(false);
  useEffect(function consumeSsrDataOnMount() {
    const ssrData = window.__SSR_DATA__;
    if (ssrData?.restaurant && ssrData.restaurant._id === id) {
      import("../../../store").then(({ default: store }) => {
        store.dispatch({
          type: "RESULTS_RECEIVED",
          payload: [ssrData.restaurant],
        });
      });
      if (ssrData.restaurantDetails) {
        setDetailsData((prev) => ({
          ...prev,
          restaurantDetails: ssrData.restaurantDetails,
        }));
      }
      delete window.__SSR_DATA__;
      setSsrConsumed(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch restaurant if not in Redux store
  useEffect(function fetchRestaurantWhenMissing() {
    if (!restaurant && !ssrConsumed) {
      getRestaurantById(id);
    }
  }, [id, restaurant, getRestaurantById, ssrConsumed]);

  // Fetch restaurant details
  useEffect(function fetchRestaurantDetails() {
    if (ssrConsumed && detailsData.restaurantDetails) return;
    async function getDetails() {
      const details = await getRestaurantDetailsById(id);
      if (details) {
        setDetailsData((prev) => ({ ...prev, restaurantDetails: details }));
      } else {
        setDetailsData((prev) => ({
          ...prev,
          restaurantMenuFallback: restaurant,
        }));
      }
    }
    getDetails();
  }, [id, restaurant, ssrConsumed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Permission checks
  const isOwnerOrAdmin = token && checkAuthz(token, ["admin", id]);
  const canEditListing =
    token && (isOwnerOrAdmin || !detailsData.restaurantDetails);

  // Edit-listing handlers (lifted from RestaurantEdit)
  const updateRestaurant = async (restaurantData) => {
    const payload = { ...restaurantData };
    if (refreshedData) {
      payload.allMenuItems = refreshedData.allMenuItems;
    }

    const config = { headers: { "Content-Type": "application/json" } };
    const body = JSON.stringify(payload);
    try {
      await axios.put("/api/restaurants/" + id, body, config);
      setAlert("Restaurant successfully updated", "success");
      setRefreshedData(null);
    } catch (err) {
      setAlert(
        err.response?.data?.errors
          ? err.response.data.errors[0].msg
          : "Error updating restaurant, please try again",
        "failure"
      );
    }
  };

  const handleRefreshClick = () => setRefreshing(true);

  const handleRefreshApply = useCallback(
    (veganOptions, allMenuItems, websiteUrl) => {
      setRefreshedData({ veganOptions, allMenuItems, websiteUrl });
      setRefreshing(false);
      setRefreshVersion((v) => v + 1);
    },
    []
  );

  const handleRefreshCancel = useCallback(() => setRefreshing(false), []);

  const restaurantForForm = restaurant
    ? refreshedData
      ? { ...restaurant, veganOptions: refreshedData.veganOptions }
      : restaurant
    : null;

  // Only show the tab bar when there's more than just the View tab
  const hasExtraTabs =
    canEditListing ||
    isOwnerOrAdmin;

  // --- Render ---

  const seoMeta = restaurant ? buildRestaurantMeta(restaurant) : null;

  const renderViewTab = () => (
    <RestaurantDetailsLayout
      restaurant={restaurant}
      searchQuery={searchParams.get("keywords")}
      onDirectionsClick={() => logRestaurantDirectionsClick(id, null, getAnalyticsSource(restaurant))}
      onWebsiteClick={() => logRestaurantWebsiteClick(id, null, getAnalyticsSource(restaurant))}
      beforeMenu={
        restaurant?.heroImage ? (
          <img
            src={restaurant.heroImage}
            alt={`${restaurant.name} hero`}
            style={{
              width: "100%",
              height: "200px",
              objectFit: "cover",
              borderRadius: "8px",
              marginTop: "8px",
              marginBottom: "8px",
            }}
          />
        ) : null
      }
      menuContent={
        detailsData.restaurantDetails ? (
          <div data-color-mode="light">
            <MDEditor.Markdown
              style={{ padding: "1em 0em" }}
              source={detailsData.restaurantDetails.menu}
            />
          </div>
        ) : detailsData.restaurantMenuFallback ? (
          <GroupedMenuDisplay
            items={getDisplayableMenuItems(detailsData.restaurantMenuFallback)}
            searchQuery={searchParams.get("keywords")}
          />
        ) : null
      }
    >
      {seoMeta && (
        <Helmet>
          <title>{seoMeta.title}</title>
          <link
            rel="canonical"
            href={`https://www.vegifyit.com/details/${id}/${computedSlug}`}
          />
          <meta name="description" content={seoMeta.description} />
        </Helmet>
      )}
    </RestaurantDetailsLayout>
  );

  const renderEditListingTab = () => (
    <PageContainer>
      <CheckUnauth />
      <PageHeader>
        <HeaderRow>
          <HeaderLeft>
            <PageTitle>
              <i className="fas fa-edit" />
              Edit Restaurant
            </PageTitle>
            <PageSubtitle>
              Update restaurant details, menu items, and cuisine information
            </PageSubtitle>
          </HeaderLeft>
          {restaurant && !refreshing && (
            <RefreshButton type="button" onClick={handleRefreshClick}>
              <i className="fas fa-sync-alt" />
              Refresh Menu
            </RefreshButton>
          )}
        </HeaderRow>
      </PageHeader>
      <ContentCard>
        {refreshing ? (
          <MenuRefreshPanel
            websiteUrl={restaurant?.website || null}
            restaurantName={restaurant?.name}
            onApply={handleRefreshApply}
            onCancel={handleRefreshCancel}
            setAlert={setAlert}
          />
        ) : restaurantForForm ? (
          <RestaurantEditForm
            key={refreshVersion}
            currRestaurant={restaurantForForm}
            submitBackendCall={updateRestaurant}
            displayText="Save Changes"
            setAlert={setAlert}
          />
        ) : (
          <LoadingState>
            <i className="fas fa-spinner" />
            <p>Loading restaurant...</p>
          </LoadingState>
        )}
      </ContentCard>
    </PageContainer>
  );

  const renderEditDescriptionTab = () => (
    <CenteredContainer>
      <CheckUnauth />
      <CenteredContent>
        {detailsData.restaurantDetails ? (
          <RestaurantDescriptionEditor
            restaurantDetails={detailsData.restaurantDetails}
            setAlert={setAlert}
          />
        ) : (
          <LoadingState>
            <i className="fas fa-spinner" />
            <p>Loading details...</p>
          </LoadingState>
        )}
      </CenteredContent>
    </CenteredContainer>
  );

  const renderAnalyticsTab = () => (
    <PageContainer>
      <CheckUnauth />
      <ContentCard>
        <RestaurantOwnerAnalytics
          restaurantId={id}
          restaurantName={restaurant?.name}
        />
      </ContentCard>
    </PageContainer>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "edit-listing":
        return canEditListing ? renderEditListingTab() : renderViewTab();
      case "edit-description":
        return isOwnerOrAdmin ? renderEditDescriptionTab() : renderViewTab();
      case "analytics":
        return isOwnerOrAdmin ? renderAnalyticsTab() : renderViewTab();
      default:
        return renderViewTab();
    }
  };

  return (
    <>
      {(hasExtraTabs || token) && (
        <TabBar>
          {hasExtraTabs && (
            <>
              <Tab $active={activeTab === "view"} onClick={() => setActiveTab("view")}>
                View
              </Tab>
              {canEditListing && (
                <Tab
                  $active={activeTab === "edit-listing"}
                  onClick={() => setActiveTab("edit-listing")}
                >
                  Edit Listing
                </Tab>
              )}
              {isOwnerOrAdmin && detailsData.restaurantDetails && (
                <Tab
                  $active={activeTab === "edit-description"}
                  onClick={() => setActiveTab("edit-description")}
                >
                  Edit Description
                </Tab>
              )}
              {isOwnerOrAdmin && (
                <Tab
                  $active={activeTab === "analytics"}
                  onClick={() => setActiveTab("analytics")}
                >
                  Analytics
                </Tab>
              )}
            </>
          )}
          {token && (
            <AddToListButton
              type="button"
              onClick={() => setShowListModal(true)}
            >
              <i className="fas fa-plus" style={{ marginRight: "6px" }} />
              Add to List
            </AddToListButton>
          )}
        </TabBar>
      )}

      {renderContent()}

      {showListModal && restaurant && (
        <AddToListModal
          restaurantId={restaurant._id}
          restaurantName={restaurant.name}
          onClose={() => setShowListModal(false)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
  restaurants: state.restaurant.restaurants,
});

export default connect(mapStateToProps, { getRestaurantById, setAlert })(
  RestaurantDetails
);

// Re-export for backward compat (ManagePopups.js imports these)
export { CenteredContainer, CenteredContent } from "./restaurantDetailsStyles";
